// A small client-only territory arena (lane C): the home screen's living backdrop (bots looping
// out and home behind the dim, territory-ui.md §7 "practice-style bot world, client-side") and,
// in dev builds, `?arenaDemo` to play the arena client against it without a server.
//
// It is NOT the game: no money, no shield, no drops, a plain capture fill. It emits real FRAME
// arena sections (LAND chunk runs, HEADS, TRAILS as RESETs, EVENTS, OWN, MINIMAP-less), so the
// backdrop runs through exactly the store, renderer and HUD code the live arena uses. Seeded and
// deterministic; the movement is the shared `stepHead`.

import {
  AMOUNT_KIND,
  ARENA_CAUSE_CODE,
  ARENA_EVENT,
  ARENA_NET,
  ARENA_PALETTE,
  ARENA_SKIN_IDS,
  ARENA_TRAIL_OP,
  type ArenaEventRecord,
  type ArenaHeadRecord,
  type ArenaLandChunk,
  type ArenaTrailRecord,
  arenaCircle,
  chunkRunsInto,
  chunksAlong,
  createRng,
  type FrameMessage,
  headingToWire,
  isPlayableCell,
  type LeaderboardEntry,
  landPctHundredths,
  playableCellCount,
  type PlayerRecord,
  type Rng,
  S2C,
  stepHead,
  trailStepsInto,
  type WelcomeArenaInfo,
} from '@ballio/shared';

const TAU = Math.PI * 2;
const DT = 0.04;

interface DemoHead {
  readonly id: number;
  readonly color: number;
  name: string;
  /** Arena skin (`""` = none): a third of demo bots wear one, like the server's (SK-5). */
  skinId: string;
  readonly bot: boolean;
  x: number;
  y: number;
  heading: number;
  target: number;
  alive: boolean;
  respawnAt: number;
  trail: number[];
  cell: number;
  land: number;
  kills: number;
  /** Bot plan: ticks left on the current leg, legs done this sortie. */
  leg: number;
  legs: number;
  turn: number;
}

const BOT_NAMES = [
  'Paperclip', 'Mosaic', 'Inkblot', 'Quilt', 'Tangram', 'Origami', 'Doodle', 'Patchwork',
  'Kite', 'Crayon', 'Stencil', 'Pixel',
]; // prettier-ignore

export interface DemoOptions {
  readonly seed?: number;
  readonly size?: number;
  readonly bots?: number;
  /** Add a player-steered head (dev `?arenaDemo`). */
  readonly player?: boolean;
}

export class DemoArena {
  readonly info: WelcomeArenaInfo;
  readonly tickRate = 25;
  private readonly cols: number;
  private readonly owner: Uint16Array;
  private readonly trailOwner: Uint16Array;
  private readonly rng: Rng;
  private readonly heads: DemoHead[] = [];
  private tick = 0;
  private readonly dirty = new Set<number>();
  private events: ArenaEventRecord[] = [];
  private announce = true;
  private snapshot = true;
  private playerTarget = 0;
  private playerSeq = 0;
  readonly playerId: number;
  private readonly slotIds: number[];

  constructor(o: DemoOptions = {}) {
    const n = o.size ?? 140;
    this.cols = n;
    this.info = {
      shape: 'circle',
      cols: n,
      rows: n,
      playableCells: playableCellCount({ shape: 'circle', cols: n, rows: n }),
      speed: 15,
      turnRateDeg: 360,
      cellsDiagonal: 58,
      zoomOutMax: 1.5,
      shieldSec: 10,
    };
    this.owner = new Uint16Array(n * n);
    this.trailOwner = new Uint16Array(n * n);
    this.rng = createRng(o.seed ?? 0xa7e4a);
    const bots = o.bots ?? 9;
    this.slotIds = [0];
    for (let i = 0; i < bots; i++) this.addHead(i + 1, true);
    this.playerId = o.player ? bots + 1 : 0;
    if (o.player) this.addHead(this.playerId, false);
    // The player (dev demo) spawns only on `respawnPlayer`.
    for (const h of this.heads) if (h.bot) this.spawn(h);
  }

  private addHead(id: number, bot: boolean): void {
    this.slotIds[id] = id;
    this.heads.push({
      id,
      color: ARENA_PALETTE[(id * 7) % ARENA_PALETTE.length] as number,
      name: bot ? (BOT_NAMES[(id - 1) % BOT_NAMES.length] as string) : 'You',
      skinId:
        bot && id % 3 === 0 ? (ARENA_SKIN_IDS[(id * 5) % ARENA_SKIN_IDS.length] as string) : '',
      bot,
      x: 0,
      y: 0,
      heading: 0,
      target: 0,
      alive: false,
      respawnAt: 0,
      trail: [],
      cell: -1,
      land: 0,
      kills: 0,
      leg: 0,
      legs: 0,
      turn: 1,
    });
  }

  /** Dev demo: (re)spawn the player now, named `name`, wearing `skinId`. */
  respawnPlayer(name: string, skinId = ''): void {
    const h = this.heads.find((q) => q.id === this.playerId);
    if (!h) return;
    h.name = name || 'You';
    h.skinId = skinId;
    if (!h.alive) this.spawn(h);
    this.announce = true;
    this.playerTarget = h.heading;
  }

  /** The player's steering (heading steps), as ARENA_INPUT would carry it. */
  steer(headingSteps: number): void {
    this.playerTarget = (headingSteps / ARENA_NET.headingSteps) * TAU;
    this.playerSeq = (this.playerSeq + 1) & 0xffff;
  }

  private playable(cx: number, cy: number): boolean {
    return isPlayableCell(this.info, cx, cy);
  }

  private spawn(h: DemoHead): void {
    const c = arenaCircle(this.info);
    for (let tries = 0; tries < 200; tries++) {
      const a = this.rng.next() * TAU;
      const d = this.rng.next() * (c.r - 12);
      const x = c.cx + Math.cos(a) * d;
      const y = c.cy + Math.sin(a) * d;
      let free = true;
      for (let dy = -6; dy <= 6 && free; dy++) {
        for (let dx = -6; dx <= 6 && free; dx++) {
          const i = (Math.floor(y) + dy) * this.cols + Math.floor(x) + dx;
          if (this.owner[i] !== 0 || this.trailOwner[i] !== 0) free = false;
        }
      }
      if (!free && tries < 199) continue;
      h.x = Math.floor(x) + 0.5;
      h.y = Math.floor(y) + 0.5;
      // The last try carves the spot free (trails there are cut short).
      for (let dy = -6; dy <= 6; dy++) {
        for (let dx = -6; dx <= 6; dx++) {
          const i = (Math.floor(y) + dy) * this.cols + Math.floor(x) + dx;
          if (i < 0 || i >= this.owner.length) continue;
          if (this.owner[i] !== 0) this.setOwner(i, 0);
          this.trailOwner[i] = 0;
        }
      }
      for (const o of this.heads) o.trail = o.trail.filter((c) => this.trailOwner[c] === o.id);
      for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
          if (dx * dx + dy * dy > 16.5) continue;
          const cx = Math.floor(x) + dx;
          const cy = Math.floor(y) + dy;
          if (this.playable(cx, cy)) this.setOwner(cy * this.cols + cx, h.id);
        }
      }
      h.heading = Math.atan2(c.cy - h.y, c.cx - h.x);
      if (h.heading < 0) h.heading += TAU;
      h.target = h.heading;
      h.alive = true;
      h.trail = [];
      h.cell = Math.floor(h.y) * this.cols + Math.floor(h.x);
      h.leg = 0;
      h.legs = 0;
      this.announce = true;
      return;
    }
  }

  private setOwner(i: number, id: number): void {
    const prev = this.owner[i] as number;
    if (prev === id) return;
    if (prev !== 0) {
      const p = this.heads.find((q) => q.id === prev);
      if (p) p.land--;
    }
    this.owner[i] = id;
    if (id !== 0) {
      const p = this.heads.find((q) => q.id === id);
      if (p) p.land++;
    }
    const s = ARENA_NET.chunkSize;
    this.dirty.add(
      Math.floor(Math.floor(i / this.cols) / s) * chunksAlong(this.cols) +
        Math.floor((i % this.cols) / s),
    );
  }

  private kill(victim: DemoHead, killer: DemoHead | null): void {
    if (!victim.alive) return;
    victim.alive = false;
    victim.respawnAt = this.tick + 75;
    for (let i = 0; i < this.owner.length; i++) {
      if (this.owner[i] === victim.id) this.setOwner(i, 0);
      if (this.trailOwner[i] === victim.id) this.trailOwner[i] = 0;
    }
    victim.trail = [];
    if (killer) killer.kills++;
    this.events.push({
      type: ARENA_EVENT.DEATH,
      victimId: victim.id,
      killerId: killer?.id ?? 0,
      cause: killer ? ARENA_CAUSE_CODE.CUT : ARENA_CAUSE_CODE.SELF,
      atWall: false,
      value: 0,
    });
  }

  private capture(h: DemoHead): void {
    for (const c of h.trail) {
      this.trailOwner[c] = 0;
      this.setOwner(c, h.id);
    }
    const n = this.cols;
    const seen = new Uint8Array(n * n);
    const stack: number[] = [];
    for (let i = 0; i < n; i++) {
      for (const c of [i, (n - 1) * n + i, i * n, i * n + n - 1]) {
        if (!seen[c] && this.owner[c] !== h.id) {
          seen[c] = 1;
          stack.push(c);
        }
      }
    }
    while (stack.length > 0) {
      const c = stack.pop() as number;
      const x = c % n;
      for (const d of [c - n, c + n, x > 0 ? c - 1 : -1, x < n - 1 ? c + 1 : -1]) {
        if (d < 0 || d >= n * n || seen[d] || this.owner[d] === h.id) continue;
        seen[d] = 1;
        stack.push(d);
      }
    }
    let gained = 0;
    for (let c = 0; c < n * n; c++) {
      if (seen[c] || this.owner[c] === h.id) continue;
      if (!this.playable(c % n, Math.floor(c / n))) continue;
      this.setOwner(c, h.id);
      gained++;
    }
    const total = h.trail.length + gained;
    h.trail = [];
    // Enemies whose head now stands on captured land die (the simple siege rule).
    for (const o of this.heads) {
      if (o === h || !o.alive) continue;
      if (this.owner[o.cell] === h.id && o.trail.length === 0) this.kill(o, h);
    }
    this.events.push({
      type: ARENA_EVENT.CAPTURE,
      playerId: h.id,
      gainedCells: total,
      cell: h.cell,
    });
  }

  private think(h: DemoHead): void {
    const n = this.cols;
    const c = arenaCircle(this.info);
    const home = this.owner[h.cell] === h.id;
    const fromCentre = Math.hypot(h.x - c.cx, h.y - c.cy);
    if (home && h.trail.length === 0) {
      if (h.leg <= 0) {
        // A new sortie: out, sideways, back.
        h.target = this.rng.next() * TAU;
        h.leg = 8 + Math.floor(this.rng.next() * 18);
        h.legs = 0;
        h.turn = this.rng.next() < 0.5 ? 1 : -1;
      }
    } else if (h.leg <= 0) {
      h.legs++;
      if (h.legs < 3) {
        h.target = h.target + (h.turn * Math.PI) / 2;
        h.leg = 6 + Math.floor(this.rng.next() * 16);
      } else {
        // Head home: toward the nearest own cell around.
        let best = -1;
        let bestD = Infinity;
        const cx = Math.floor(h.x);
        const cy = Math.floor(h.y);
        for (let r = 1; r < 40 && best < 0; r += 2) {
          for (let dy = -r; dy <= r; dy++) {
            for (let dx = -r; dx <= r; dx++) {
              const i = (cy + dy) * n + cx + dx;
              if (i < 0 || i >= n * n || this.owner[i] !== h.id) continue;
              const d = dx * dx + dy * dy;
              if (d < bestD) {
                bestD = d;
                best = i;
              }
            }
          }
        }
        if (best >= 0)
          h.target = Math.atan2(Math.floor(best / n) + 0.5 - h.y, (best % n) + 0.5 - h.x);
        h.leg = 4;
      }
    }
    if (fromCentre > c.r - 6) h.target = Math.atan2(c.cy - h.y, c.cx - h.x);
    h.target = ((h.target % TAU) + TAU) % TAU;
    h.leg--;
  }

  /** The head enters `cell` (trail, cut, capture); false when it died. */
  private enter(h: DemoHead, cell: number): boolean {
    const left = h.cell;
    h.cell = cell;
    const tOwner = this.trailOwner[cell] as number;
    if (tOwner !== 0) {
      const victim = this.heads.find((q) => q.id === tOwner);
      if (victim === h) {
        this.kill(h, null);
        return false;
      }
      if (victim) this.kill(victim, h);
    }
    if (this.owner[cell] === h.id) {
      if (h.trail.length > 0) this.capture(h);
    } else if (this.owner[left] !== h.id && left >= 0 && this.trailOwner[left] === 0) {
      // Every cell left while outside own land becomes trail (spec §3).
      this.trailOwner[left] = h.id;
      h.trail.push(left);
    }
    return true;
  }

  /** One tick; returns the FRAME this client would receive. */
  step(): FrameMessage {
    this.tick++;
    for (const h of this.heads) {
      if (!h.alive) {
        if (h.bot && this.tick >= h.respawnAt) this.spawn(h);
        continue;
      }
      if (h.bot) this.think(h);
      else h.target = this.playerTarget;
      const ox = h.x;
      const oy = h.y;
      stepHead(h, h.target, { speed: this.info.speed, turnRateRad: TAU, dt: DT }, this.info);
      // Cell entries along the segment (fine sampling is enough here).
      const steps = Math.ceil(Math.hypot(h.x - ox, h.y - oy) / 0.2);
      for (let k = 1; k <= steps && h.alive; k++) {
        const x = ox + ((h.x - ox) * k) / steps;
        const y = oy + ((h.y - oy) * k) / steps;
        const cell = Math.floor(y) * this.cols + Math.floor(x);
        if (cell === h.cell) continue;
        const pcx = h.cell % this.cols;
        const pcy = Math.floor(h.cell / this.cols);
        const ncx = cell % this.cols;
        const ncy = Math.floor(cell / this.cols);
        // A diagonal step goes through a side cell first (4-connected, like the sim's supercover).
        if (pcx !== ncx && pcy !== ncy && !this.enter(h, pcy * this.cols + ncx)) break;
        if (h.alive && !this.enter(h, cell)) break;
      }
    }
    return this.frame();
  }

  private frame(): FrameMessage {
    const msg: FrameMessage = { op: S2C.FRAME, tick: this.tick };
    if (this.announce) {
      this.announce = false;
      msg.players = this.heads.map((h): PlayerRecord => ({
        playerId: h.id,
        partyMember: false,
        isYou: h.id === this.playerId,
        teammate: false,
        isBot: h.bot,
        color: h.color,
        team: 0,
        name: h.name,
        skinId: h.skinId,
      }));
    }
    const chunks: ArenaLandChunk[] = [];
    const list = this.snapshot
      ? Array.from({ length: chunksAlong(this.cols) ** 2 }, (_, i) => i)
      : [...this.dirty];
    for (const chunk of list) {
      const runs: number[] = [];
      chunkRunsInto(this.owner, this.cols, this.cols, chunk, this.slotIds, runs);
      chunks.push({ chunk, runs });
    }
    msg.arenaLand = { snapshot: this.snapshot, chunks };
    this.snapshot = false;
    this.dirty.clear();
    msg.arenaHeads = this.heads
      .filter((h) => h.alive)
      .map((h): ArenaHeadRecord => ({
        playerId: h.id,
        x: Math.round(h.x * 256) / 256,
        y: Math.round(h.y * 256) / 256,
        heading: headingToWire(h.heading),
        shielded: false,
        cashingOut: false,
        frozen: false,
        autopilot: false,
        alarm: false,
        progress: 0,
      }));
    const trails: ArenaTrailRecord[] = [];
    for (const h of this.heads) {
      if (h.trail.length === 0) {
        trails.push({ playerId: h.id, op: ARENA_TRAIL_OP.CLEAR, startCell: 0, steps: [] });
        continue;
      }
      const steps: number[] = [];
      const gap = trailStepsInto(h.trail, 0, h.trail.length - 1, this.cols, steps);
      if (gap >= 0) continue;
      trails.push({
        playerId: h.id,
        op: ARENA_TRAIL_OP.RESET,
        startCell: h.trail[0] as number,
        steps,
      });
    }
    msg.arenaTrails = trails;
    if (this.events.length > 0) {
      msg.arenaEvents = this.events;
      this.events = [];
    }
    const me = this.heads.find((h) => h.id === this.playerId);
    if (me?.alive) msg.arenaOwn = { landCells: me.land, kills: me.kills, inputSeq: this.playerSeq };
    if (this.tick % 5 === 0) {
      const ranked = this.heads.filter((h) => h.alive).sort((a, b) => b.land - a.land);
      const entries: LeaderboardEntry[] = ranked.slice(0, 5).map((h) => ({
        isYou: h.id === this.playerId,
        partyMember: false,
        isBot: h.bot,
        name: h.name,
        amount: h.land,
        landPct: landPctHundredths(h.land, this.info.playableCells) / 100,
      }));
      const rank = me?.alive ? ranked.indexOf(me) + 1 : 0;
      msg.leaderboard = {
        entries,
        yourRank: rank,
        amountKind: AMOUNT_KIND.SCORE,
        yourAmount: me?.land ?? 0,
        yourLandPct: me ? landPctHundredths(me.land, this.info.playableCells) / 100 : 0,
      };
    }
    return msg;
  }

  /** The demo's top bot (the backdrop camera follows it). */
  leaderId(): number {
    let best: DemoHead | null = null;
    for (const h of this.heads) if (h.alive && h.bot && (!best || h.land > best.land)) best = h;
    return best?.id ?? 0;
  }

  /** Whether the player's head is alive (dev demo). */
  get playerAlive(): boolean {
    return this.heads.find((h) => h.id === this.playerId)?.alive ?? false;
  }
}
